'use client';

import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import { useCart } from '@/features/checkout/CartProvider';
import {
  interpolateCheckoutMessage,
  readCheckoutMessage,
} from '@/features/checkout/checkoutMessages';
import { hasCheckoutShipping } from '@/lib/shipping';
import Link from 'next/link';
import { Check } from 'react-bootstrap-icons';
import '../checkout.scss';

const CHECKOUT_STEPS = [
  {
    id: 'cart',
    href: '/cart',
    labelKey: 'cart',
    compactLabelKey: 'cartCompact',
    fallbackLabel: 'Cart',
    fallbackCompactLabel: 'Cart',
    step: 1,
  },
  {
    id: 'signin',
    labelKey: 'signIn',
    compactLabelKey: 'signInCompact',
    fallbackLabel: 'Sign In',
    fallbackCompactLabel: 'Sign in',
    step: 2,
  },
  {
    id: 'shipping',
    href: '/shipping',
    labelKey: 'shipping',
    compactLabelKey: 'shippingCompact',
    fallbackLabel: 'Shipping',
    fallbackCompactLabel: 'Ship',
    step: 3,
  },
  {
    id: 'payment',
    href: '/payment',
    labelKey: 'payment',
    compactLabelKey: 'paymentCompact',
    fallbackLabel: 'Payment',
    fallbackCompactLabel: 'Pay',
    step: 4,
  },
  {
    id: 'placeorder',
    href: '/placeorder',
    labelKey: 'placeOrder',
    compactLabelKey: 'placeOrderCompact',
    fallbackLabel: 'Place Order',
    fallbackCompactLabel: 'Review',
    step: 5,
  },
];

function getStepAvailability({
  currentStep,
  hasHydrated,
  isAuthenticated,
  hasItems,
  hasPaymentMethod,
  hasShippingDetails,
  step,
}) {
  if (!hasHydrated) {
    return step <= Math.max(1, currentStep);
  }

  switch (step) {
    case 1:
      return true;
    case 2:
      return hasItems;
    case 3:
      return hasItems && isAuthenticated;
    case 4:
      return hasItems && isAuthenticated && hasShippingDetails;
    case 5:
      return (
        currentStep >= 4 &&
        hasItems &&
        isAuthenticated &&
        hasShippingDetails &&
        hasPaymentMethod
      );
    default:
      return false;
  }
}

export default function CheckoutSteps({
  currentStep = 1,
  isAuthenticated = false,
  messages = {},
}) {
  const {
    cartItems,
    geolocation,
    hasHydrated,
    paymentMethod,
    shippingAddress,
  } = useCart();
  const hasItems = cartItems.length > 0;
  const hasShippingDetails = hasCheckoutShipping(shippingAddress, geolocation);
  const hasPaymentMethod = Boolean(paymentMethod);
  const stepMessage = (path, fallback = '') =>
    readCheckoutMessage(messages, 'checkoutSteps', path) || fallback;

  return (
    <nav
      className="checkout-steps-nav mb-5"
      aria-label={stepMessage('navAria', 'Checkout progress')}
    >
      <ol className="d-flex flex-nowrap justify-content-center align-items-start list-unstyled m-0">
        {CHECKOUT_STEPS.map((entry) => {
          const label = stepMessage(entry.labelKey, entry.fallbackLabel);
          const compactLabel = stepMessage(
            entry.compactLabelKey,
            entry.fallbackCompactLabel || label,
          );
          const isActive = currentStep === entry.step;
          const isComplete =
            !isActive &&
            ((entry.step === 1 && currentStep > 1) ||
              (entry.step === 2 && isAuthenticated) ||
              (entry.step === 3 && currentStep > 3 && hasShippingDetails) ||
              (entry.step === 4 && currentStep > 4 && hasPaymentMethod));
          const isAvailable = getStepAvailability({
            currentStep,
            hasHydrated,
            isAuthenticated,
            hasItems,
            hasPaymentMethod,
            hasShippingDetails,
            step: entry.step,
          });
          const href =
            entry.id === 'signin'
              ? !isAuthenticated
                ? buildAuthHref('/login', '/shipping')
                : ''
              : entry.href;
          const linkClassName = `checkout-step-link ${
            isComplete ? 'completed' : ''
          } ${isActive ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`.trim();
          const ariaLabel = interpolateCheckoutMessage(
            stepMessage(
              isAvailable ? 'stepAria' : 'stepAriaDisabled',
              isAvailable
                ? 'Checkout step {{step}}: {{label}}'
                : 'Checkout step {{step}} (disabled): {{label}}',
            ),
            {
              step: entry.step,
              label,
            },
          );
          const content = (
            <>
              <span
                className={`step-icon-wrapper ${isComplete ? 'surface-2' : 'surface-1'}`.trim()}
                aria-hidden="true"
              >
                {isComplete ? (
                  <span className="step-number">
                    <Check className="step-icon completed-icon" size={18} />
                  </span>
                ) : (
                  <span className="step-number">{entry.step}</span>
                )}
              </span>
              <span className="step-label text-nowrap">{label}</span>
              <span className="step-label step-label--compact text-nowrap">
                {compactLabel || label}
              </span>
            </>
          );

          return (
            <li key={entry.id} className="checkout-step-item">
              {href && isAvailable ? (
                <Link
                  href={href}
                  className={linkClassName}
                  aria-label={ariaLabel}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {content}
                </Link>
              ) : (
                <span
                  className={linkClassName}
                  aria-label={ariaLabel}
                  aria-current={isActive ? 'step' : undefined}
                  aria-disabled={!isAvailable}
                >
                  {content}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
